import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Printer, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface PrintSettingsDialogProps {
  testId: string;
  testData: {
    title: string;
    grade: string;
    subject: string;
    topic: string;
    time_limit?: number;
    total_questions: number;
  };
}

interface PrintSettings {
  includeExplanations: boolean;
  includeAnswers: boolean;
  pointsPerQuestion: number;
  showTimeLimit: boolean;
  headerInfo: boolean;
  studentInfo: boolean;
  pageNumbers: boolean;
  fontSize: 'small' | 'medium' | 'large';
  spacing: 'compact' | 'normal' | 'spacious';
}

export default function PrintSettingsDialog({ testId, testData }: PrintSettingsDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<PrintSettings>({
    includeExplanations: false,
    includeAnswers: true,
    pointsPerQuestion: 1,
    showTimeLimit: true,
    headerInfo: true,
    studentInfo: true,
    pageNumbers: true,
    fontSize: 'medium',
    spacing: 'normal'
  });

  const handlePrint = async (downloadPdf = false) => {
    try {
      // Fetch questions
      const { data: questions, error } = await supabase
        .from('instructor_test_questions')
        .select(`
          id,
          question_order,
          points,
          custom_question_data,
          question_id,
          questions:question_id (
            id,
            text,
            explanation,
            options (
              id,
              text,
              is_correct
            )
          )
        `)
        .eq('test_id', testId)
        .order('question_order');

      if (error) throw error;

      // Resolve subject name if it's a GUID
      let resolvedSubject = testData.subject;
      if (testData.subject && testData.subject.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        const { data: subjectData, error: subjectError } = await supabase
          .from('subjects')
          .select('name')
          .eq('id', testData.subject)
          .single();
        
        if (!subjectError && subjectData) {
          resolvedSubject = subjectData.name;
        }
      }

      // Generate print HTML
      const printHtml = generatePrintHTML(questions || [], { ...settings, resolvedSubject });
      
      if (downloadPdf) {
        // Create a temporary div for PDF generation
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = printHtml;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '210mm';
        tempDiv.style.fontSize = '14px';
        tempDiv.style.lineHeight = '1.4';
        tempDiv.style.fontFamily = 'Arial, sans-serif';
        document.body.appendChild(tempDiv);
        
        try {
          const pdf = new jsPDF('p', 'mm', 'a4');
          const pageHeight = pdf.internal.pageSize.getHeight() - 40; // Leave margins
          const pageWidth = pdf.internal.pageSize.getWidth() - 40;
          
          // Get all question elements
          const questionElements = tempDiv.querySelectorAll('.question');
          let currentPageHeight = 40; // Start with top margin
          let pageCount = 1;
          
          // Add header to first page
          if (settings.headerInfo) {
            const headerElement = tempDiv.querySelector('.header');
            if (headerElement) {
              pdf.setFontSize(16);
              pdf.setFont('Arial', 'bold');
              pdf.text(testData.title, 20, currentPageHeight);
              currentPageHeight += 10;
              
              pdf.setFontSize(10);
              pdf.setFont('Arial', 'normal');
              pdf.text(`Grade: ${testData.grade} | Subject: ${resolvedSubject} | Topic: ${testData.topic}`, 20, currentPageHeight);
              currentPageHeight += 8;
              
              if (settings.showTimeLimit && testData.time_limit) {
                pdf.text(`Time Limit: ${testData.time_limit} minutes | Total Questions: ${testData.total_questions}`, 20, currentPageHeight);
                currentPageHeight += 8;
              }
              
              pdf.text(`Date: ${new Date().toLocaleDateString()}`, 20, currentPageHeight);
              currentPageHeight += 15;
            }
          }
          
          // Add student info section
          if (settings.studentInfo) {
            pdf.setFontSize(12);
            pdf.setFont('Arial', 'bold');
            pdf.text('Student Information', 20, currentPageHeight);
            currentPageHeight += 10;
            
            pdf.setFontSize(10);
            pdf.setFont('Arial', 'normal');
            pdf.text('Name: ___________________________    Date: ___________________    Score: ________', 20, currentPageHeight);
            currentPageHeight += 15;
          }
          
            // Process each question
          questionElements.forEach((questionEl, index) => {
            const questionData = questions[index];
            const customData = questionData?.custom_question_data as any;
            const questionText = (customData && typeof customData === 'object' && 'text' in customData) 
              ? customData.text 
              : 'Question not available';
            const questionType = (customData && typeof customData === 'object' && 'type' in customData) 
              ? customData.type 
              : 'unknown';
            const points = settings.pointsPerQuestion > 0 ? settings.pointsPerQuestion : questionData?.points || 1;
            
            // Check if we need a new page
            const estimatedQuestionHeight = 40 + (questionType === 'multiple_choice' ? 20 : 15);
            if (currentPageHeight + estimatedQuestionHeight > pageHeight && pageCount > 0) {
              pdf.addPage();
              pageCount++;
              currentPageHeight = 20;
            }
            
            // Add question header
            pdf.setFontSize(12);
            pdf.setFont('Arial', 'bold');
            pdf.text(`Question ${index + 1} (${points} point${points !== 1 ? 's' : ''})`, 20, currentPageHeight);
            currentPageHeight += 8;
            
            // Add question text
            pdf.setFontSize(10);
            pdf.setFont('Arial', 'normal');
            const splitText = pdf.splitTextToSize(questionText, pageWidth - 20);
            pdf.text(splitText, 20, currentPageHeight);
            currentPageHeight += (splitText.length * 5) + 5;
            
            // Add question-specific content
            if (customData && typeof customData === 'object') {
              if (questionType === 'multiple_choice' && 'options' in customData && Array.isArray(customData.options)) {
                customData.options.forEach((option: any, optIndex: number) => {
                  const optionText = `${String.fromCharCode(65 + optIndex)}. ${option.text || option}`;
                  const correctIndicator = settings.includeAnswers && option.is_correct ? ' ✓' : '';
                  pdf.text(optionText + correctIndicator, 25, currentPageHeight);
                  currentPageHeight += 5;
                });
              } else if (questionType === 'true_false' && 'correct_answer' in customData) {
                const correctAnswer = customData.correct_answer;
                pdf.text(`○ True ${settings.includeAnswers && correctAnswer === true ? '✓' : ''}`, 25, currentPageHeight);
                currentPageHeight += 5;
                pdf.text(`○ False ${settings.includeAnswers && correctAnswer === false ? '✓' : ''}`, 25, currentPageHeight);
                currentPageHeight += 5;
              } else if (questionType === 'matching' && 'pairs' in customData && Array.isArray(customData.pairs)) {
                pdf.text('Match the following:', 25, currentPageHeight);
                currentPageHeight += 5;
                customData.pairs.forEach((pair: any, idx: number) => {
                  pdf.text(`${idx + 1}. ${pair.left || ''}`, 25, currentPageHeight);
                  pdf.text(`${String.fromCharCode(65 + idx)}. ${pair.right || ''}`, 100, currentPageHeight);
                  currentPageHeight += 5;
                });
                if (settings.includeAnswers) {
                  const answerKey = customData.pairs.map((_: any, idx: number) => `${idx + 1}-${String.fromCharCode(65 + idx)}`).join(', ');
                  pdf.text(`Answer Key: ${answerKey}`, 25, currentPageHeight);
                  currentPageHeight += 5;
                }
              } else if (questionType === 'fill_blank' && 'blanks' in customData && Array.isArray(customData.blanks) && settings.includeAnswers) {
                pdf.text(`Answers: ${customData.blanks.join(', ')}`, 25, currentPageHeight);
                currentPageHeight += 5;
              } else if (questionType === 'puzzle' && 'correct_answer' in customData && settings.includeAnswers) {
                pdf.text(`Answer: ${customData.correct_answer}`, 25, currentPageHeight);
                currentPageHeight += 5;
              }
            }
            
            // Add explanation if enabled
            if (settings.includeExplanations && customData && typeof customData === 'object' && 'explanation' in customData && customData.explanation) {
              pdf.setFont('Arial', 'italic');
              pdf.text(`Explanation: ${customData.explanation}`, 25, currentPageHeight);
              currentPageHeight += 8;
            }
            
            currentPageHeight += 10; // Space between questions
            pdf.setFont('Arial', 'normal');
          });
          
          // Add page numbers if enabled
          if (settings.pageNumbers) {
            const totalPages = pdf.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              pdf.setFontSize(8);
              pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 10, pageHeight + 30);
            }
          }
          
          pdf.save(`${testData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_questions.pdf`);
          toast.success('PDF downloaded successfully!');
          
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          toast.error('Failed to generate PDF. Please try print preview instead.');
        } finally {
          document.body.removeChild(tempDiv);
        }
        
        setIsOpen(false);
        return;
      }

      // Open print preview
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printHtml);
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Error preparing print:', error);
      toast.error('Failed to prepare print document');
    }
  };

  const generatePrintHTML = (questions: any[], settings: PrintSettings & { resolvedSubject?: string }) => {
    const fontSizeClass = {
      small: '12px',
      medium: '14px',
      large: '16px'
    }[settings.fontSize];

    const spacingClass = {
      compact: '15px',
      normal: '25px',
      spacious: '35px'
    }[settings.spacing];

    const questionsHtml = questions.map((q, index) => {
      const questionData = q.custom_question_data || q.questions;
      if (!questionData || typeof questionData !== 'object') return '';

      const questionText = (questionData && 'text' in questionData) ? questionData.text : 'Question text not available';
      const explanation = (questionData && 'explanation' in questionData) ? questionData.explanation : null;
      const points = settings.pointsPerQuestion > 0 ? settings.pointsPerQuestion : q.points;

      let contentHtml = '';

      // Handle different question types
      if (questionData.type === 'multiple_choice' && questionData.options) {
        contentHtml = questionData.options.map((option: any, optIndex: number) => {
          const isCorrect = option.is_correct;
          const correctIndicator = settings.includeAnswers && isCorrect ? ' ✓' : '';
          return `
            <div class="option ${settings.includeAnswers && isCorrect ? 'correct-answer' : ''}">
              ${String.fromCharCode(65 + optIndex)}. ${option.text}${correctIndicator}
            </div>
          `;
        }).join('');
      } else if (questionData.type === 'true_false') {
        const correctAnswer = questionData.correct_answer;
        contentHtml = `
          <div class="tf-options">
            <div class="option ${settings.includeAnswers && correctAnswer === true ? 'correct-answer' : ''}">
              ○ True ${settings.includeAnswers && correctAnswer === true ? '✓' : ''}
            </div>
            <div class="option ${settings.includeAnswers && correctAnswer === false ? 'correct-answer' : ''}">
              ○ False ${settings.includeAnswers && correctAnswer === false ? '✓' : ''}
            </div>
          </div>
        `;
      } else if (questionData.type === 'matching' && questionData.pairs) {
        const leftItems = questionData.pairs.map((pair: any, idx: number) => `
          <div class="match-item">${idx + 1}. ${pair.left}</div>
        `).join('');
        
        const rightItems = questionData.pairs.map((pair: any, idx: number) => `
          <div class="match-item">${String.fromCharCode(65 + idx)}. ${pair.right}</div>
        `).join('');

        contentHtml = `
          <div class="matching-container">
            <div class="match-column">
              <h4>Column A</h4>
              ${leftItems}
            </div>
            <div class="match-column">
              <h4>Column B</h4>
              ${rightItems}
            </div>
          </div>
          ${settings.includeAnswers ? `
            <div class="answer-key">
              <strong>Answer Key:</strong> 
              ${questionData.pairs.map((pair: any, idx: number) => `${idx + 1}-${String.fromCharCode(65 + idx)}`).join(', ')}
            </div>
          ` : ''}
        `;
      } else if (questionData.type === 'fill_blank') {
        contentHtml = `
          <div class="fill-blank">
            ${settings.includeAnswers && questionData.blanks ? `
              <div class="answer-key">
                <strong>Answers:</strong> ${questionData.blanks.join(', ')}
              </div>
            ` : ''}
          </div>
        `;
      } else if (questionData.type === 'puzzle') {
        contentHtml = `
          <div class="puzzle">
            ${settings.includeAnswers && questionData.correct_answer ? `
              <div class="answer-key">
                <strong>Answer:</strong> ${questionData.correct_answer}
              </div>
            ` : ''}
          </div>
        `;
      }

      return `
        <div class="question" style="margin-bottom: ${spacingClass};">
          <div class="question-header">
            <span class="question-number">Question ${index + 1}</span>
            <span class="question-points">(${points} point${points !== 1 ? 's' : ''})</span>
          </div>
          <div class="question-text">${questionText}</div>
          ${contentHtml}
          ${settings.includeExplanations && explanation ? `
            <div class="explanation">
              <strong>Explanation:</strong> ${explanation}
            </div>
          ` : ''}
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Test Questions - ${testData.title}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
              .page-break { page-break-before: always; }
            }
            
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: ${fontSizeClass};
              line-height: 1.4;
            }
            
            .header { 
              border-bottom: 2px solid #333; 
              padding-bottom: 15px; 
              margin-bottom: 25px; 
            }
            
            .header h1 { 
              margin: 0 0 10px 0; 
              font-size: 24px;
            }
            
            .header-info { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 20px; 
              margin-top: 10px;
            }
            
            .student-info {
              border: 1px solid #ddd;
              padding: 15px;
              margin-bottom: 20px;
              background-color: #f9f9f9;
            }
            
            .student-info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 20px;
            }
            
            .info-field {
              border-bottom: 1px solid #333;
              padding-bottom: 5px;
            }
            
            .question { 
              margin-bottom: ${spacingClass}; 
              border: 1px solid #ddd; 
              padding: 15px;
              break-inside: avoid;
            }
            
            .question-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #eee;
              padding-bottom: 5px;
            }
            
            .question-text { 
              font-weight: bold; 
              margin-bottom: 10px; 
            }
            
            .option { 
              margin: 8px 0; 
              padding: 4px 0;
            }
            
            .correct-answer { 
              background-color: #e8f5e8;
              padding: 4px;
              border-radius: 3px;
            }
            
            .tf-options {
              margin: 10px 0;
            }
            
            .matching-container {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin: 15px 0;
            }
            
            .match-column h4 {
              margin: 0 0 10px 0;
              text-decoration: underline;
            }
            
            .match-item {
              margin: 5px 0;
              padding: 3px 0;
            }
            
            .answer-key {
              margin-top: 10px;
              padding: 8px;
              background-color: #f0f8ff;
              border-left: 4px solid #2196F3;
              font-size: 0.9em;
            }
            
            .explanation { 
              margin-top: 10px; 
              padding: 8px;
              background-color: #fff8dc;
              border-left: 4px solid #ffa500;
              font-style: italic; 
              font-size: 0.9em;
            }
            
            .page-footer {
              position: fixed;
              bottom: 20px;
              right: 20px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          ${settings.headerInfo ? `
            <div class="header">
              <h1>${testData.title}</h1>
              <div class="header-info">
                <div>
                  <strong>Grade:</strong> ${testData.grade}<br>
                  <strong>Subject:</strong> ${settings.resolvedSubject || testData.subject}<br>
                  <strong>Topic:</strong> ${testData.topic}
                </div>
                <div>
                  ${settings.showTimeLimit && testData.time_limit ? `<strong>Time Limit:</strong> ${testData.time_limit} minutes<br>` : ''}
                  <strong>Total Questions:</strong> ${testData.total_questions}<br>
                  <strong>Date:</strong> ${new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          ` : ''}
          
          ${settings.studentInfo ? `
            <div class="student-info">
              <h3 style="margin: 0 0 15px 0;">Student Information</h3>
              <div class="student-info-grid">
                <div>
                  <strong>Name:</strong>
                  <div class="info-field">&nbsp;</div>
                </div>
                <div>
                  <strong>Date:</strong>
                  <div class="info-field">&nbsp;</div>
                </div>
                <div>
                  <strong>Score:</strong>
                  <div class="info-field">&nbsp;</div>
                </div>
              </div>
            </div>
          ` : ''}
          
          <div class="questions-container">
            ${questionsHtml || '<p>No questions found.</p>'}
          </div>
          
          ${settings.pageNumbers ? '<div class="page-footer no-print">Page 1</div>' : ''}
        </body>
      </html>
    `;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Printer className="h-4 w-4 mr-2" />
          Print Questions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Print Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Content Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Content Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="include-answers">Include Answers</Label>
                <Switch 
                  id="include-answers"
                  checked={settings.includeAnswers}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, includeAnswers: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="include-explanations">Include Explanations</Label>
                <Switch 
                  id="include-explanations"
                  checked={settings.includeExplanations}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, includeExplanations: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="show-time">Show Time Limit</Label>
                <Switch 
                  id="show-time"
                  checked={settings.showTimeLimit}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showTimeLimit: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="header-info">Test Header Info</Label>
                <Switch 
                  id="header-info"
                  checked={settings.headerInfo}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, headerInfo: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="student-info">Student Info Section</Label>
                <Switch 
                  id="student-info"
                  checked={settings.studentInfo}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, studentInfo: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="page-numbers">Page Numbers</Label>
                <Switch 
                  id="page-numbers"
                  checked={settings.pageNumbers}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, pageNumbers: checked }))}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Layout Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Layout Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Font Size</Label>
                <Select 
                  value={settings.fontSize} 
                  onValueChange={(value: any) => setSettings(prev => ({ ...prev, fontSize: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (12px)</SelectItem>
                    <SelectItem value="medium">Medium (14px)</SelectItem>
                    <SelectItem value="large">Large (16px)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Question Spacing</Label>
                <Select 
                  value={settings.spacing} 
                  onValueChange={(value: any) => setSettings(prev => ({ ...prev, spacing: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="spacious">Spacious</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Points per Question (0 = use individual)</Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="10"
                  value={settings.pointsPerQuestion}
                  onChange={(e) => setSettings(prev => ({ ...prev, pointsPerQuestion: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handlePrint(true)}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={() => handlePrint(false)}>
              <Printer className="h-4 w-4 mr-2" />
              Print Preview
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}